/**
 * @todo When the legacy theme is removed: review whether the component-based dialog
 * implementations need any legacy-theme fallback work removed.
 */

/** @typedef {import('quasar').DialogChainObject} DialogChainObject */

import { Dialog } from 'quasar';

import HrAlertDialog from '../primitives/dialogs/HrAlertDialog.vue';
import HrConfirmDestructiveDialog from '../primitives/dialogs/HrConfirmDestructiveDialog.vue';
import HrConfirmDialog from '../primitives/dialogs/HrConfirmDialog.vue';
import HrOptionsDialog from '../primitives/dialogs/HrOptionsDialog.vue';
import HrPromptDialog from '../primitives/dialogs/HrPromptDialog.vue';

/**
 * Open a dialog that renders an arbitrary Vue component as its content.
 *
 * @param {object} component - Vue component definition
 * @param {object} [props] - Props forwarded to the component via `componentProps`
 * @param {(payload?: unknown) => void} [onOk] - Called when the component emits `ok`
 * @param {() => void} [onCancel] - Called when the component emits `cancel`
 * @param {() => void} [onDismiss] - Called when the dialog is dismissed for any reason
 * @param {import('quasar').QDialogOptions} [opts] - Additional Quasar dialog options
 * @returns {DialogChainObject}
 */
const openComponentDialog = (component, props, onOk, onCancel, onDismiss, opts = {}) => {
  const dialog = Dialog.create({
    component,
    ...opts,
    componentProps: props,
  });

  if (onOk) {
    dialog.onOk(onOk);
  }

  if (onCancel) {
    dialog.onCancel(onCancel);
  }

  if (onDismiss) {
    dialog.onDismiss(onDismiss);
  }

  return dialog;
};

/**
 * Open an M3 confirm dialog with OK and Cancel actions.
 * Does nothing if `onOk` is not provided.
 *
 * @param {string} title
 * @param {string} message
 * @param {() => void} onOk - Called when the user confirms
 * @param {{ confirmLabel?: string, cancelLabel?: string, persistent?: boolean }} [opts]
 * @param {() => void} [onCancel]
 * @returns {DialogChainObject | undefined}
 */
const confirmDialog = (title, message, onOk, opts = {}, onCancel = () => {}) => {
  if (!onOk) {
    return;
  }

  return openComponentDialog(HrConfirmDialog, { title, message, ...opts }, onOk, onCancel);
};

/**
 * Open an M3 destructive confirm dialog. The confirm action is styled with the error role.
 * Does nothing if `onOk` is not provided.
 *
 * @param {string} title
 * @param {string} message
 * @param {() => void} onOk - Called when the user confirms the destructive action
 * @param {{ confirmLabel: string, cancelLabel?: string, persistent?: boolean }} [opts]
 * @param {() => void} [onCancel]
 * @returns {DialogChainObject | undefined}
 */
const confirmDestructiveDialog = (title, message, onOk, opts = {}, onCancel = () => {}) => {
  if (!onOk) {
    return;
  }

  if (!opts.confirmLabel) {
    throw new Error('confirmDestructiveDialog requires an explicit confirmLabel');
  }

  return openComponentDialog(
    HrConfirmDestructiveDialog,
    { title, message, ...opts },
    onOk,
    onCancel,
  );
};

/**
 * Open an M3 alert dialog with a single OK action.
 *
 * @param {string} title
 * @param {string} message
 * @param {{ okLabel?: string }} [opts]
 * @returns {DialogChainObject}
 */
const alertDialog = (title, message, opts) => {
  return openComponentDialog(HrAlertDialog, { title, message, ...opts }, () => {});
};

/**
 * Open an M3 prompt dialog with a text input field.
 * Does nothing if `onOk` is not provided.
 *
 * @param {string} title
 * @param {string} message
 * @param {(value: string) => void} onOk - Called with the entered value when the user confirms
 * @param {{ model?: string, label?: string, isValid?: (v: string) => boolean,
 *   confirmLabel?: string, cancelLabel?: string, persistent?: boolean }} [opts]
 * @param {() => void} [onCancel]
 * @returns {DialogChainObject | undefined}
 */
const promptDialog = (title, message, onOk, opts = {}, onCancel = () => {}) => {
  if (!onOk) {
    return;
  }

  return openComponentDialog(HrPromptDialog, { title, message, ...opts }, onOk, onCancel);
};

/**
 * Open an M3 radio dialog. The user selects one option and confirms.
 * Does nothing if `onOk` is not provided.
 *
 * @param {string} title
 * @param {string | null} message - Supporting text, or null to omit
 * @param {Array<{ label: string, value: unknown, disable?: boolean }>} items
 * @param {(value: unknown) => void} onOk - Called with the selected value when the user confirms
 * @param {{ model?: unknown, isValid?: (v: unknown) => boolean,
 *   confirmLabel?: string, cancelLabel?: string, persistent?: boolean }} [opts]
 * @param {() => void} [onCancel]
 * @returns {DialogChainObject | undefined}
 */
const radioDialog = (title, message, items, onOk, opts = {}, onCancel = () => {}) => {
  if (!onOk) {
    return;
  }

  return openComponentDialog(
    HrOptionsDialog,
    { title, message, items, type: 'radio', ...opts },
    onOk,
    onCancel,
  );
};

/**
 * Open an M3 checkbox dialog. The user selects one or more options and confirms.
 * Does nothing if `onOk` is not provided.
 *
 * @param {string} title
 * @param {string | null} message - Supporting text, or null to omit
 * @param {Array<{ label: string, value: unknown, disable?: boolean }>} items
 * @param {(values: unknown[]) => void} onOk - Called with the array of selected values when confirmed
 * @param {{ model?: unknown[], isValid?: (v: unknown[]) => boolean,
 *   confirmLabel?: string, cancelLabel?: string, persistent?: boolean }} [opts]
 * @param {() => void} [onCancel]
 * @returns {DialogChainObject | undefined}
 */
const checkboxDialog = (title, message, items, onOk, opts = {}, onCancel = () => {}) => {
  if (!onOk) {
    return;
  }

  return openComponentDialog(
    HrOptionsDialog,
    { title, message, items, type: 'checkbox', ...opts },
    onOk,
    onCancel,
  );
};

/**
 * Composable returning imperative M3 dialog helpers. Safe to call outside of a setup context.
 *
 * @returns {{ confirmDialog: Function, confirmDestructiveDialog: Function,
 *   alertDialog: Function, promptDialog: Function, radioDialog: Function,
 *   checkboxDialog: Function, openComponentDialog: Function }}
 */
// eslint-disable-next-line import/prefer-default-export
const useDialog = () => ({
  confirmDialog,
  confirmDestructiveDialog,
  alertDialog,
  promptDialog,
  radioDialog,
  checkboxDialog,
  openComponentDialog,
});

export {
  alertDialog,
  checkboxDialog,
  confirmDestructiveDialog,
  confirmDialog,
  openComponentDialog,
  promptDialog,
  radioDialog,
  useDialog,
};
